import { ref, type Ref } from 'vue';
import {
  isTextOverflowing,
  useOverflowTooltip,
  type UseOverflowTooltipOptions,
} from '@/composables/useOverflowTooltip';
import { useLazyTooltip } from '@/composables/useLazyTooltip';

/**
 * Lazy wrapper around {@link useOverflowTooltip}: render a plain truncated element by default and
 * only mount the reka-ui Tooltip stack (and its ResizeObserver) on the first hover or keyboard
 * focus of a cell whose content actually overflows. A results table can be hundreds of cells, and
 * eagerly mounting a tooltip per cell is what froze the page on load. Cells the user never hovers
 * cost nothing beyond the plain element.
 *
 * The activation itself goes through {@link useLazyTooltip}, the same path LazyTooltip uses. That
 * is where the pointer-left check and the normal open delay live.
 *
 * Wiring in a component:
 *   - on the plain (pre-activation) element: `ref="probeRef"` plus
 *     `@pointerover="onProbePointerOver" @pointerout="onProbePointerOut"`
 *     `@focusin="onProbeFocusIn" @focusout="onProbeFocusOut"`;
 *   - once `activated`, render the Tooltip with `ref="textRef"` on the trigger and
 *     `:open="tooltipOpen" @update:open="onOpenChange"`.
 *   - when the hoverable trigger is larger than the measured text, such as a button around a
 *     truncated span, put `ref="triggerRootRef"` on the outer element. The pointer then counts
 *     as still on the trigger anywhere inside it.
 *
 * The cell components can't all share the OverflowTooltip *component* (their triggers differ:
 * span, link, button; and AdminAppLinkColumn has an import cycle via ModelRecordInspector), but
 * they can share this composable.
 */
export function useLazyOverflowTooltip(displayDeps: Ref<unknown>, options?: UseOverflowTooltipOptions) {
  const { textRef, tooltipOpen, onOpenChange } = useOverflowTooltip(displayDeps, options);
  const triggerRootRef = ref<HTMLElement | null>(null);

  const { activated, probeRef, onProbePointerOver, onProbePointerOut, onProbeFocusIn, onProbeFocusOut } =
    useLazyTooltip({
      open: () => onOpenChange(true),
      close: () => onOpenChange(false),
      triggerElement: () => triggerRootRef.value ?? textRef.value,
      canActivate: (probeElement) => !!probeElement && isTextOverflowing(probeElement),
    });

  return {
    activated,
    probeRef,
    triggerRootRef,
    onProbePointerOver,
    onProbePointerOut,
    onProbeFocusIn,
    onProbeFocusOut,
    textRef,
    tooltipOpen,
    onOpenChange,
  };
}
